import { Controller } from "@hotwired/stimulus"

/**
 * Universal scroll-to and highlight controller.
 *
 * Usage:
 *   <div data-controller="scroll-highlight"
 *        data-scroll-highlight-param-value="highlight_ca_id"
 *        data-scroll-highlight-attribute-value="data-conversion-action-id">
 *     <tr data-conversion-action-id="123">...</tr>
 *   </div>
 *
 * Reads the URL param specified by `param`, finds the first element
 * within this controller's element that has a matching `attribute` value,
 * scrolls to it, and highlights it with an outline that fades when the user clicks anywhere.
 *
 * If the matched element is inside a collapsed tip-open section,
 * the controller will auto-expand it before scrolling.
 *
 * If the target isn't found on connect (e.g. inside a lazy turbo frame),
 * listens for turbo:frame-load and retries once the content arrives.
 */
export default class extends Controller {
  static values = {
    param: String,
    attribute: String
  }

  connect() {
    const params = new URLSearchParams(window.location.search)
    this.targetId = params.get(this.paramValue)
    if (!this.targetId) return

    // Try immediately — if content is already in the DOM
    if (this.findAndHighlight()) return

    // Otherwise wait for turbo frame content to arrive
    this.frameLoadHandler = () => this.findAndHighlight()
    this.element.addEventListener("turbo:frame-load", this.frameLoadHandler)
  }

  disconnect() {
    if (this.frameLoadHandler) {
      this.element.removeEventListener("turbo:frame-load", this.frameLoadHandler)
    }
    if (this.clearHighlight) {
      document.removeEventListener("click", this.clearHighlight)
    }
  }

  findAndHighlight() {
    const selector = `[${this.attributeValue}="${this.targetId}"]`
    const target = this.element.querySelector(selector)
    if (!target) return false

    // Clean up listener if we were waiting
    if (this.frameLoadHandler) {
      this.element.removeEventListener("turbo:frame-load", this.frameLoadHandler)
      this.frameLoadHandler = null
    }

    this.expandCollapsedAncestors(target)

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "center" })

      target.style.outline = "3px solid #EF8143"
      target.style.outlineOffset = "-2px"

      this.clearHighlight = () => {
        setTimeout(() => {
          target.style.transition = "outline-color .4s ease"
          target.style.outlineColor = "transparent"
          document.removeEventListener("click", this.clearHighlight)
          this.clearHighlight = null
        }, 2500);
      }

      document.addEventListener("click", this.clearHighlight)
    })

    return true
  }

  expandCollapsedAncestors(target) {
    let el = target.closest("[data-tip-open-is-open-value]")
    while (el) {
      if (el.dataset.tipOpenIsOpenValue === "false") {
        const content = el.querySelector("[data-tip-open-target='content']")
        const icon = el.querySelector("[data-tip-open-target='icon']")

        if (content) content.classList.remove("hidden")
        if (icon) icon.classList.add("rotate-180")
        el.dataset.tipOpenIsOpenValue = "true"
      }
      el = el.parentElement?.closest("[data-tip-open-is-open-value]")
    }
  }
}
